import { FC, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { UserCheck } from 'lucide-react';
import { funnelsAPI, plateformesAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { Dropdown, DropdownItem } from 'components/forms/Dropdown';
import { RadioGroupComponent } from 'components/forms/RadioGroup';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { ModalShell, ModalSection } from 'back-office/shared/components/ModalShell';
import { DASH } from '../funnelFormat';

type PlateformeMode = 'tunnel' | 'other';

const MODE_OPTIONS: { value: PlateformeMode; label: string }[] = [
  { value: 'tunnel', label: 'De la plateforme du tunnel' },
  { value: 'other', label: "D'une autre plateforme" },
];

interface AddValidatorModalProps {
  tunnelId: string;
  /**
   * Platforms known to belong to the tunnel. Empty or absent when unknown: both modes
   * then offer every platform.
   */
  tunnelPlateformeIds?: string[];
  onSuccess: () => void;
}

/** Adds a collaborator holding the Valideur brick to the validators of a tunnel. */
export const AddValidatorModal: FC<AddValidatorModalProps> = ({
  tunnelId,
  tunnelPlateformeIds,
  onSuccess,
}) => {
  const { closeDialog } = useDialog();
  const [mode, setMode] = useState<PlateformeMode>('tunnel');
  const [plateformeId, setPlateformeId] = useState<string | null>(null);
  const [abpUserId, setAbpUserId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchPlateformes = useCallback(() => plateformesAPI.getPlateformes(), []);
  const plateformes = useAsyncResource(fetchPlateformes);

  const fetchValidators = useCallback(
    () =>
      funnelsAPI.getFunnelAvailableValidators({ tunnelId, plateformeId: plateformeId as string }),
    [tunnelId, plateformeId]
  );
  const validators = useAsyncResource(fetchValidators, Boolean(plateformeId));

  const plateformeItems = useMemo<DropdownItem<string>[]>(() => {
    const known = new Set(tunnelPlateformeIds ?? []);
    return (plateformes.data?.plateformes ?? [])
      .filter((plateforme) => {
        if (known.size === 0) return true;
        return mode === 'tunnel' ? known.has(plateforme.id) : !known.has(plateforme.id);
      })
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'fr'))
      .map((plateforme) => ({ key: plateforme.id, label: plateforme.name || DASH }));
  }, [plateformes.data, tunnelPlateformeIds, mode]);

  // Results of a previous platform stay in the resource until the new request ends.
  const validatorItems = useMemo<DropdownItem<string>[]>(
    () =>
      plateformeId
        ? (validators.data?.items ?? [])
            .filter((item) => item.plateformeId === plateformeId)
            .map((item) => ({
              key: item.abpUserId,
              label: `${item.nom ?? DASH} — ${item.cabinet ?? DASH}`,
              disabled: item.dejaValideur,
              trailingLabel: item.dejaValideur ? 'déjà valideur' : undefined,
            }))
        : [],
    [validators.data, plateformeId]
  );

  const changeMode = (next: PlateformeMode) => {
    setMode(next);
    setPlateformeId(null);
    setAbpUserId(null);
  };

  const changePlateforme = (item: DropdownItem<string>) => {
    setPlateformeId(item.key);
    setAbpUserId(null);
  };

  const submit = async () => {
    if (!plateformeId || !abpUserId) return;

    setIsSubmitting(true);
    try {
      const response = await funnelsAPI.addFunnelValidator({
        tunnelId,
        addTunnelValideurCommand: { abpUserId, plateformeId },
      });
      if (response.isSuccess) {
        toast.success('Valideur ajouté');
        onSuccess();
        closeDialog();
      } else {
        toast.error(response.errorMessage || "L'ajout du valideur a échoué");
      }
    } catch (error) {
      console.error('Ajout du valideur impossible', error);
      const parsed = await parseApiError(error);
      toast.error(parsed.message || "L'ajout du valideur a échoué");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Ajouter un valideur"
      subtitle="Collaborateur portant la brique Valideur"
      isLoading={plateformes.isLoading}
      submit={{
        label: 'Ajouter',
        onClick: submit,
        disabled: !plateformeId || !abpUserId || isSubmitting,
      }}
    >
      <ModalSection title="Collaborateur" icon={<UserCheck className="h-4 w-4" />}>
        <div className="flex flex-col gap-4">
          <RadioGroupComponent<PlateformeMode>
            name="plateformeMode"
            value={mode}
            options={MODE_OPTIONS}
            onChange={changeMode}
          />

          {plateformes.error ? (
            <p className="text-sm text-red-500">{plateformes.error}</p>
          ) : (
            <Dropdown
              name="plateformeId"
              label="Plateforme"
              items={plateformeItems}
              selectedItem={plateformeItems.find((item) => item.key === plateformeId) ?? null}
              onSelect={(item) => changePlateforme(item as DropdownItem<string>)}
              placeholder="Sélectionner une plateforme"
            />
          )}

          {plateformeId &&
            (validators.isLoading ? (
              <p className="text-sm text-gray-400">Chargement des valideurs…</p>
            ) : validators.error ? (
              <p className="text-sm text-red-500">{validators.error}</p>
            ) : validatorItems.length === 0 ? (
              <p className="text-sm italic text-gray-500">
                Aucun collaborateur de cette plateforme ne porte la brique Valideur.
              </p>
            ) : (
              <Dropdown
                name="abpUserId"
                label="Valideur"
                items={validatorItems}
                selectedItem={validatorItems.find((item) => item.key === abpUserId) ?? null}
                onSelect={(item) => setAbpUserId(item.key as string)}
                placeholder="Sélectionner un collaborateur"
              />
            ))}
        </div>
      </ModalSection>
    </ModalShell>
  );
};
